import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import refundPayment from '@/shared/api/orders/payments/refundPayment';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import Payment from '@/shared/types/order/payment.type';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Dialog = ReturnType<typeof useModalDialog<unknown, DialogName>>['dialog'];

type Args = {
  dialog: Dialog;
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
};

const usePaymentsOperations = ({
  dialog,
  onUpdateDialogState,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
}: Args) => {
  const { t } = useTranslation(['payments', 'common']);

  const redirectLogin = useRedirectLogin();

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);

  const onRefundPayment = async () => {
    onUpdateDialogState(DialogName.IssueRefund, { loading: true });
    onUpdateDialogState(DialogName.IssueRefundConfirmation, { open: false });

    onUpdateProgressDialog({
      title: t('payments:issuingRefund'),
      open: true,
    });

    const { amount, transactionId, orderId } = dialog?.issueRefundConfirmation
      ?.data as {
      amount: number;
      transactionId: string;
      orderId: string;
    };

    const response = await refundPayment({
      orderId,
      transactionId,
      body: { amount },
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateDialogState(DialogName.IssueRefund, { open: false });
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('payments:refundIssued'),
        severity: 'success',
      });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.IssueRefund, {
        loading: false,
        errors: [response.msg],
      });
    }
  };

  const onVoidTransaction = async () => {
    onUpdateDialogState(DialogName.VoidTransactionConfirmation, {
      open: false,
    });

    onUpdateProgressDialog({
      title: t('payments:voidingRefund'),
      open: true,
    });

    const { in_transaction_id, in_order_id } = dialog
      ?.voidTransactionConfirmation?.data as Payment;

    const response = await refundPayment({
      orderId: in_order_id,
      transactionId: in_transaction_id,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('payments:refundVoided'),
        severity: 'success',
      });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.VoidTransactionErrors, {
        open: true,
        errors: [response.msg],
      });
    }
  };

  return { refreshTableIndex, onRefundPayment, onVoidTransaction };
};

export default usePaymentsOperations;
